import { fetchAuthSession, signInWithRedirect } from "aws-amplify/auth";
import { asNumberOrThrow, asStringOrThrow } from "../../../shared/type_utils";
import { createDataStore } from "../lib/data_store";
import { listAccountData } from "./account_store";
import { setAccountError } from "./account_error_store";

export interface User {
  username: string;
  name: string;
  guid: string;
  email: string;
  loggedInAt: Date;
  expiresAt: Date;
}

const userStore = createDataStore<User | undefined>();

export const useUser = userStore.useDataSuspense;
export const setUser = userStore.setData;

function handleIdToken(idToken: {
  payload: Record<string, unknown>;
  toString: () => string;
}): void {
  // Clean up OAuth query parameters from URL
  const url = new URL(window.location.href);
  if (url.searchParams.has("code") || url.searchParams.has("state")) {
    window.history.replaceState({}, document.title, url.pathname);
  }
  // Set user data from token claims
  setUser({
    username: asStringOrThrow(idToken.payload["cognito:username"]),
    name: asStringOrThrow(idToken.payload.name),
    guid: asStringOrThrow(idToken.payload["custom:guid"]),
    email: asStringOrThrow(idToken.payload.email),
    loggedInAt: new Date(asNumberOrThrow(idToken.payload.iat) * 1000),
    expiresAt: new Date(asNumberOrThrow(idToken.payload.exp) * 1000),
  });
  // Fetch initial account data after successful login.
  listAccountData().catch((err) => {
    console.error("Failed to list account data:", err);
    setAccountError(true);
  });
}

export const fetchUser = () => {
  fetchAuthSession()
    .then(({ tokens }) => {
      const idToken = tokens?.idToken;
      if (idToken === undefined) {
        // Not authenticated — redirect to Cognito login.
        // In testonly mode, show the hosted UI so e2e tests can fill credentials.
        // In all other modes, redirect directly to the SSO provider.
        if (import.meta.env.MODE === "testonly") {
          signInWithRedirect();
        } else {
          signInWithRedirect({ provider: { custom: "AWS-SSO" } });
        }
      } else {
        handleIdToken(idToken);
      }
    })
    .catch((error) => console.error("Error fetching user:", error));
};
